import { useCallback, useRef, useState } from "react";
import * as turf from "@turf/turf";

const ADM3_URL = "/DATA/OSMbased_adm3_boundary.json";

// Nominatim's top-level "name"/category can just be the enclosing
// suburb/village/road when no real POI sits at this exact point - these
// categories are area/administrative, never a landmark, so a match tagged
// with one of these is rejected rather than returned as if it were a landmark.
const AREA_CATEGORIES = new Set(["place", "boundary", "highway", "natural", "landuse", "administrative"]);

// The boundary file is ~25MB - fetched once per browser session (module-
// level cache) no matter how many felt-report forms use this hook or how
// many times a user opens one.
let adm3DataPromise = null;
const loadAdm3Data = () => {
    if (!adm3DataPromise) {
        adm3DataPromise = fetch(ADM3_URL).then((r) => r.json());
    }
    return adm3DataPromise;
};

// A suco's ADM_Code is hierarchical (e.g. "TL040601"): the first 4 chars are
// its municipality code, the first 6 its sub-municipality code, matching
// exactly how the Municipality/SubMunicipality/Suco master tables store
// adm1_code/adm2_code/adm3_code - so once a point resolves to a suco, the
// whole hierarchy is just substrings of that one code, no extra lookup.
const splitAdmCode = (admCode) => ({
    adm1_code: admCode.slice(0, 4),
    adm2_code: admCode.slice(0, 6),
    adm3_code: admCode,
});

// Shared by every felt-report submission form: wraps the browser's
// geolocation prompt and reverse-geocodes the result (or any point the user
// later drags/clicks to) against the adm3 suco boundaries, so a form can
// auto-fill Municipality/Post-Admin/Suco instead of requiring them to be
// picked first. Manual dropdown selection still works exactly as before -
// this only ever pre-fills it; nothing here disables the dropdowns.
export const useLiveLocation = () => {
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState("");
    const adm3FeaturesRef = useRef(null);

    const ensureAdm3Data = useCallback(async () => {
        if (adm3FeaturesRef.current) return adm3FeaturesRef.current;
        const data = await loadAdm3Data();
        adm3FeaturesRef.current = data.features || [];
        return adm3FeaturesRef.current;
    }, []);

    // Point-in-polygon against every suco boundary. Returns the matched
    // ADM hierarchy (codes + names) or null if the point falls outside
    // every known boundary (e.g. offshore, or just outside the country) -
    // callers fall back to manual selection in that case.
    const resolveAdmFromLatLng = useCallback(async (lat, lon) => {
        const features = await ensureAdm3Data();
        const point = turf.point([lon, lat]);
        let match = null;
        for (const feature of features) {
            try {
                if (turf.booleanPointInPolygon(point, feature)) {
                    match = feature;
                    break;
                }
            } catch (err) {
                // malformed feature geometry - skip it, keep checking others
            }
        }
        if (!match || !match.properties?.ADM_Code) return null;
        const { adm1_code, adm2_code, adm3_code } = splitAdmCode(match.properties.ADM_Code);
        return {
            adm1_code, adm2_code, adm3_code,
            adm1: match.properties.ADM1 || "",
            adm2: match.properties.ADM2 || "",
            adm3: match.properties.ADM3 || "",
        };
    }, [ensureAdm3Data]);

    // One-shot request (not a live watch) - resolves once with the
    // position the browser's permission prompt yields, or rejects with a
    // human-readable reason a form can show next to its manual dropdowns.
    const requestBrowserLocation = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                const message = "Geolocation isn't supported by this browser - select the location manually below.";
                setLocationError(message);
                reject(new Error(message));
                return;
            }
            setLocating(true);
            setLocationError("");
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocating(false);
                    resolve({ lat: position.coords.latitude, lon: position.coords.longitude });
                },
                (error) => {
                    setLocating(false);
                    const message = error.code === error.PERMISSION_DENIED
                        ? "Location permission was denied - select the location manually below."
                        : "Couldn't get your location - select it manually below.";
                    setLocationError(message);
                    reject(new Error(message));
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        });
    }, []);

    // Nearest named place/landmark for a point, via OpenStreetMap's
    // Nominatim reverse-geocoding service (free, no key) at zoom=18 - the
    // finest detail level it offers (building/POI level). Returns null if
    // nothing nameable is found there (open field, etc.) so the caller can
    // prompt the user to type their own landmark instead of silently
    // leaving it blank. fetch() has no built-in timeout, so this aborts
    // after 8s rather than leaving the caller (and "Looking up nearest
    // landmark..." UI) hanging forever if Nominatim is slow. NOTE:
    // Nominatim's usage policy caps this around 1 request/sec and asks
    // callers to identify themselves - fine for one lookup per user action
    // like this, but if usage ever grows this should move behind a backend
    // proxy instead of calling it directly from the browser.
    const resolveLandmark = useCallback(async (lat, lon) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
                { headers: { Accept: "application/json" }, signal: controller.signal }
            );
            if (!res.ok) return null;
            const data = await res.json();
            const addr = data.address || {};

            // Only trusted when the matched feature's own category is a
            // genuine place/POI type (amenity, shop, tourism, building,
            // etc.), never an administrative/area/road category.
            const isNamedPoi = data.name && !AREA_CATEGORIES.has(data.category) && !AREA_CATEGORIES.has(data.type);
            if (isNamedPoi) return data.name;

            // Address-component fallback: only fields that represent an
            // actual named building/POI. Deliberately excludes
            // road/neighbourhood/suburb/village/city - those are area names,
            // not landmarks, and must fall through to "no landmark found".
            const namedBuilding = addr.building && addr.building !== "yes" ? addr.building : null;
            return (
                addr.amenity || addr.shop || addr.tourism || addr.office ||
                addr.historic || addr.leisure || addr.craft || addr.place_of_worship ||
                namedBuilding ||
                null
            );
        } catch (err) {
            return null;
        } finally {
            clearTimeout(timeoutId);
        }
    }, []);

    return { locating, locationError, setLocationError, requestBrowserLocation, resolveAdmFromLatLng, resolveLandmark };
};

export default useLiveLocation;
